import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { getPostHogClient, initializePostHog } from '@cloud-ui/components/posthog/posthogClient';
import { callApi } from '@cloud-ui/utils/api';
import { initializeSentry } from '@cloud-ui/utils/browserTelemetry';
import type { ConfigDTO } from '@shared/dto';

interface ConfigContextType {
  config: ConfigDTO | null;
  isLoading: boolean;
  error: Error | null;
  browserTelemetryReady: boolean;
}

const ConfigContext = createContext<ConfigContextType | null>(null);

async function fetchConfig(): Promise<ConfigDTO> {
  const response = await callApi('/config');
  if (!response.ok) {
    throw new Error('Failed to load config');
  }
  return response.json();
}

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [browserTelemetryReady, setBrowserTelemetryReady] = useState(false);
  const {
    data: config,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['config'],
    queryFn: fetchConfig,
    staleTime: Infinity, // Config won't change during session
    retry: 2,
  });

  useEffect(() => {
    if (config?.disableTelemetry === true) {
      getPostHogClient()?.opt_out_capturing();
      Sentry.close();
      setBrowserTelemetryReady(false);
    } else if (config) {
      initializePostHog();
      initializeSentry();
      getPostHogClient()?.opt_in_capturing();
      setBrowserTelemetryReady(true);
    }
  }, [config]);

  return (
    <ConfigContext.Provider
      value={{
        config: config || null,
        isLoading,
        error: error || null,
        browserTelemetryReady,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }

  return {
    ...context,
    ...(context.config || {}),
  } as ConfigContextType & ConfigDTO;
}
